import React, { useEffect, useRef, useState } from "react";
import IrabUpsell from "./IrabUpsell";
import useArabicFieldRemap from "./typing/useArabicFieldRemap";
import { conjugateVerb, fetchConjugatorConfig } from "../api";
import { buildParadigmTables } from "../utils/paradigmTables";
import { tagsToLabel } from "./ParadigmSheet";
import { track } from "../utils/analytics";

// Shareable link for a conjugated verb: /sarf?text=… prepopulates and
// auto-conjugates for whoever opens it (their visit is a cache hit — the
// sharer's lookup already warmed it). Same host convention as shareIrabUrl.
function shareSarfUrl(word) {
  const base = window.location.hostname === "my.muraja.app"
    ? "https://muraja.app"
    : window.location.origin;
  return `${base}/sarf?text=${encodeURIComponent(word)}`;
}

/**
 * SarfPanel — the Verb Conjugator's working surface, shared by the in-app
 * overlay (SarfTool.js) and the public page (PublicSarfPage.js). Type any
 * verb form → lemma, root, wazn, derivatives, and the full paradigm tables.
 */
export default function SarfPanel({ onClose, initialText = "", autoAnalyze = false, context = "overlay" }) {
  const [text, setText] = useState(initialText);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [upsell, setUpsell] = useState(null); // "quota" | null
  const [upsellRetryAt, setUpsellRetryAt] = useState(null);
  const [result, setResult] = useState(null); // conjugate response json
  const [maxChars, setMaxChars] = useState(30);
  const [remaining, setRemaining] = useState(null);
  const [subscriberDaily, setSubscriberDaily] = useState(null);
  const [tier, setTier] = useState(null);
  const [shareState, setShareState] = useState("idle"); // idle | copied
  const inputRef = useRef(null);
  const autoRan = useRef(false);
  const shareResetRef = useRef(null);
  const { remapOn, toggleRemap, remapKeyDown } = useArabicFieldRemap();

  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    inputRef.current?.focus();
    fetchConjugatorConfig()
      .then((cfg) => {
        setMaxChars(cfg.max_chars || 30);
        setRemaining(cfg.lookups_remaining_today);
        setSubscriberDaily(cfg.subscriber_daily_lookups);
        setTier(cfg.tier);
      })
      .catch(() => {}); // panel still works; server enforces
  }, []);

  useEffect(() => () => clearTimeout(shareResetRef.current), []);

  const conjugate = async (word) => {
    const trimmed = (word || "").trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setError("");
    setUpsell(null);
    try {
      const data = await conjugateVerb(trimmed);
      setResult(data);
      setTier(data.tier || tier);
      if (data.remaining_today != null) setRemaining(data.remaining_today);
      track("sarf-analyze", { context, source: data.source, is_verb: data.is_verb });
    } catch (err) {
      if (err.status === 429 && err.signupCta) {
        setUpsell("quota");
        setUpsellRetryAt(err.retryAt);
      } else {
        setError(err.message || "Failed to conjugate the verb");
      }
    } finally {
      setLoading(false);
    }
  };

  const submit = () => conjugate(text);

  // ?text= handoff (SEO page / shared links) — same contract as IrabPanel.
  useEffect(() => {
    if (initialText) setText((t) => t || initialText);
  }, [initialText]);

  useEffect(() => {
    if (autoAnalyze && initialText && !autoRan.current) {
      autoRan.current = true;
      conjugate(initialText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAnalyze, initialText]);

  const share = async () => {
    const url = shareSarfUrl(result?.result?.lemma || text.trim());
    track("sarf-share-click", { context });
    const touchDevice = window.matchMedia?.("(pointer: coarse)")?.matches;
    if (navigator.share && touchDevice) {
      try {
        await navigator.share({ title: "تصريف الأفعال — Verb Conjugator", url });
      } catch { /* user dismissed the sheet */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareState("copied");
      clearTimeout(shareResetRef.current);
      shareResetRef.current = setTimeout(() => setShareState("idle"), 2000);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const onInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
      return;
    }
    remapKeyDown(e, setText);
  };

  const counterBits = [`${text.length}/${maxChars}`];
  if (remaining != null) counterBits.push(`${remaining} new verbs left today`);

  const verb = result?.is_verb ? result.result : null;
  const paradigm = verb ? buildParadigmTables(verb.conjugations) : null;

  return (
    <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 shrink-0" style={{ borderBottom: "1px solid var(--panel-line)" }}>
        <div className="flex items-center gap-2.5">
          <span className="text-xl select-none">◈</span>
          <div>
            <h2 className="text-base font-semibold text-dt-parchment leading-tight">Conjugator — تصريف الأفعال</h2>
            <p className="text-xs text-dt-parchment-dim leading-tight">
              Any verb form → root, wazn, and the full paradigm
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-dt-parchment-dim hover:text-dt-parchment text-xl leading-none p-1"
          >
            ✕
          </button>
        )}
      </div>

      {/* Input */}
      <div className="px-5 pt-4 shrink-0">
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, maxChars))}
          onKeyDown={onInputKeyDown}
          dir="rtl"
          lang="ar"
          placeholder="اُكْتُبْ فِعْلًا هُنَا… (يكتبون، اِسْتَغْفَرَ، قُل)"
          className="dt-field w-full px-3.5 py-2.5 rounded-xl font-amiri text-2xl leading-relaxed"
        />
        <div className="flex items-center justify-between gap-3 mt-2.5">
          <div className="flex items-center gap-3 text-xs text-dt-parchment-dim">
            <button
              onClick={toggleRemap}
              title="Type Arabic on a US keyboard (same layout as the typing lessons)"
              className={`px-2 py-1 rounded-lg border transition-colors ${remapOn ? "text-dt-gold-bright" : ""}`}
              style={{ borderColor: "var(--panel-line)" }}
            >
              ⌨ع {remapOn ? "on" : "off"}
            </button>
            <span>{counterBits.join(" · ")}</span>
          </div>
          <button
            onClick={submit}
            disabled={loading || !text.trim()}
            className="px-4 py-2 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold transition-[filter]"
          >
            {loading ? "Conjugating…" : "Conjugate — صَرِّف"}
          </button>
        </div>
      </div>

      {/* Errors / upsell */}
      {error && !upsell && (
        <div className="mx-5 mt-3 px-3.5 py-2.5 rounded-xl text-sm shrink-0"
          style={{ background: "var(--tag-rose-bg)", border: "1px solid var(--panel-line)", color: "var(--tag-rose)" }}>
          {error}
        </div>
      )}
      {upsell && (
        <IrabUpsell kind="quota" retryAt={upsellRetryAt} subscriberDaily={subscriberDaily} context={`sarf-${context}`} />
      )}

      {/* Result */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {result && !result.is_verb && (
          <div className="px-3.5 py-2.5 rounded-xl text-sm"
            style={{ background: "var(--tag-orange-bg)", border: "1px solid var(--panel-line)", color: "var(--tag-orange)" }}>
            {result.detail || "This word is not an Arabic verb."}
          </div>
        )}

        {verb && (
          <>
            {/* Lemma header card */}
            <div className="rounded-xl px-4 py-3.5 mb-4" style={{ border: "1px solid var(--panel-line)" }}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-3" dir="rtl">
                  <span className="font-amiri text-4xl text-dt-gold-bright">{verb.lemma}</span>
                  {verb.masdar && <span className="font-amiri text-xl text-dt-parchment-dim">{verb.masdar}</span>}
                </div>
                <button
                  onClick={share}
                  title="Copy a link that opens this verb, conjugated"
                  className="px-2.5 py-1 rounded-full text-xs border transition-colors hover:text-dt-gold-bright"
                  style={{
                    color: shareState === "copied" ? "var(--gold-bright)" : "var(--parchment-dim)",
                    borderColor: shareState === "copied" ? "var(--gold)" : "var(--panel-line)",
                  }}
                >
                  {shareState === "copied" ? "Link copied ✓" : "⤴ Share"}
                </button>
              </div>
              {verb.definition && (
                <p className="text-sm text-dt-parchment mt-1">{verb.definition}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-2.5 text-xs">
                {verb.root && <Chip label="Root" value={verb.root} />}
                {verb.form && <Chip label="Form" value={verb.pattern ? `${verb.form} — ${verb.pattern}` : verb.form} />}
                {verb.masdar && <Chip label="Masdar" value={verb.masdar} />}
                {verb.ism_faail && <Chip label="Active participle" value={verb.ism_faail} />}
                {verb.ism_mafool && <Chip label="Passive participle" value={verb.ism_mafool} />}
              </div>
              {verb.matched && (
                <p className="text-xs text-dt-parchment-dim mt-2.5">
                  {/* <bdi> isolates the Arabic so it can't reorder the
                      English around it (e.g. splitting "3rd") */}
                  You looked up <bdi className="font-amiri text-base text-dt-parchment">{verb.matched.text}</bdi>
                  {" — "}{tagsToLabel(verb.matched.tags)}
                </p>
              )}
            </div>

            {/* Ambiguous skeleton: other readings */}
            {result.alternatives?.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4 text-xs text-dt-parchment-dim">
                <span>Also matches:</span>
                {result.alternatives.map((alt) => (
                  <button
                    key={alt.lemma}
                    onClick={() => { setText(alt.lemma); conjugate(alt.lemma); }}
                    className="px-2.5 py-1 rounded-full border transition-colors hover:text-dt-gold-bright"
                    style={{ borderColor: "var(--panel-line)" }}
                  >
                    <span className="font-amiri text-sm">{alt.lemma}</span>
                    {alt.form && <span className="ms-1.5 opacity-75">({alt.form})</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Paradigm tables */}
            <div className="grid gap-4 sm:grid-cols-2">
              {paradigm.tables.map((table) => (
                <section key={table.key} className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--panel-line)" }}>
                  <header className="flex items-baseline justify-between px-3.5 py-2" style={{ borderBottom: "1px solid var(--panel-line)" }}>
                    <span className="text-xs font-semibold text-dt-parchment">{table.label}</span>
                    <span className="font-amiri text-base text-dt-gold-bright">{table.labelAr}</span>
                  </header>
                  <ul>
                    {table.rows.map((r, i) => (
                      <li
                        key={`${r.text}-${i}`}
                        dir="rtl"
                        title={r.slotLabel || tagsToLabel(r.tags)}
                        className="flex items-baseline justify-between gap-3 px-3.5 py-1"
                        style={i > 0 ? { borderTop: "1px solid var(--panel-line)" } : undefined}
                      >
                        <span className="font-amiri text-sm text-dt-parchment-dim w-14 shrink-0">{r.pronoun}</span>
                        <span className="font-amiri text-xl text-dt-parchment flex-1 text-start">{r.text}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            {/* Same-root words */}
            {result.related?.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold text-dt-parchment mb-2">
                  Built on the same root {verb.root && <span className="font-amiri text-sm text-dt-gold-bright ms-1">{verb.root}</span>}
                </p>
                <div className="flex flex-wrap gap-2">
                  {result.related.map((rel) => (
                    rel.is_verb ? (
                      <button
                        key={rel.text}
                        onClick={() => { setText(rel.text); conjugate(rel.text); }}
                        className="px-3 py-1.5 rounded-xl border text-start transition-colors hover:text-dt-gold-bright"
                        style={{ borderColor: "var(--panel-line)" }}
                      >
                        <span className="font-amiri text-lg">{rel.text}</span>
                        {rel.definition && <span className="block text-[11px] text-dt-parchment-dim">{rel.definition}</span>}
                      </button>
                    ) : (
                      <span
                        key={rel.text}
                        className="px-3 py-1.5 rounded-xl border"
                        style={{ borderColor: "var(--panel-line)" }}
                      >
                        <span className="font-amiri text-lg text-dt-parchment">{rel.text}</span>
                        {rel.definition && <span className="block text-[11px] text-dt-parchment-dim">{rel.definition}</span>}
                      </span>
                    )
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!result && !loading && (
          <div className="text-center text-sm text-dt-parchment-dim py-10">
            Type any verb — even a conjugated form like يكتبون — to get its
            root, wazn, and every table: past, present, imperative, and passive.
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ label, value }) {
  return (
    <span className="px-2.5 py-1 rounded-full border" style={{ borderColor: "var(--panel-line)" }}>
      <span className="text-dt-parchment-dim">{label}: </span>
      <span className="font-amiri text-sm text-dt-parchment">{value}</span>
    </span>
  );
}
